/**
 * Store Locator - JavaScript functionality
 * Uses Google Maps
 */

(function() {
  'use strict';

  // =========================================================================
  // Module-scoped state (not on window)
  // =========================================================================

  let map = null;
  let markers = [];
  let markerCluster = null;
  let stores = [];
  let activePopup = null;
  let userLocation = null;
  let referenceLocation = null;
  let referenceMarker = null;
  let markerIconCache = {};

  // Get shared utilities
  const utils = window.StoreLocatorUtils || {};

  // Configuration (set from Liquid)
  const config = window.storeLocatorConfig || {
    apiKey: '',
    defaultLat: 50.0755,
    defaultLng: 14.4378,
    defaultZoom: 5,
    enableClustering: true,
    markerStyle: 'flag',
    strings: {}
  };

  // DOM Elements
  const elements = {
    map: null,
    searchInput: null,
    searchBtn: null,
    locateBtn: null,
    countryFilter: null,
    storeList: null,
    resultsCount: null,
    detailPanel: null,
    detailContent: null,
    detailClose: null,
    serviceFilters: null
  };

  // =========================================================================
  // Utility Functions
  // =========================================================================

  /**
   * Escape HTML to prevent XSS
   * @param {string|object} text - Text to escape
   * @returns {string} Escaped HTML string
   */
  function escapeHtml(text) {
    if (utils.escapeHtml) {
      return utils.escapeHtml(text);
    }
    if (!text) return '';
    if (typeof text === 'object') {
      text = text.value || text.toString();
    }
    const div = document.createElement('div');
    div.textContent = String(text);
    return div.innerHTML;
  }

  /**
   * Convert degrees to radians
   * @param {number} deg - Degrees
   * @returns {number} Radians
   */
  function toRad(deg) {
    return deg * (Math.PI / 180);
  }

  /**
   * Calculate distance between two points (Haversine formula)
   * @param {{lat: number, lng: number}} p1 - First point
   * @param {{lat: number, lng: number}} p2 - Second point
   * @returns {number} Distance in kilometers
   */
  function calculateDistance(p1, p2) {
    if (utils.calculateDistanceFromPoints) {
      return utils.calculateDistanceFromPoints(p1, p2);
    }
    const R = 6371;
    const dLat = toRad(p2.lat - p1.lat);
    const dLng = toRad(p2.lng - p1.lng);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(toRad(p1.lat)) * Math.cos(toRad(p2.lat)) *
              Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * Format distance for display
   * @param {number} km - Distance in kilometers
   * @returns {string} Formatted distance string
   */
  function formatDistance(km) {
    if (utils.formatDistance) {
      return utils.formatDistance(km);
    }
    if (km < 1) {
      return Math.round(km * 1000) + ' m';
    }
    return km.toFixed(1) + ' km';
  }

  /**
   * Normalize country code to ISO 3166-1 alpha-2
   * Fixes common mistakes like 'slo' for Slovenia (should be 'si')
   * @param {string} code - Country code to normalize
   * @returns {string} Normalized ISO 3166-1 alpha-2 code
   */
  function normalizeCountryCode(code) {
    if (!code) return '';

    const normalized = code.toLowerCase().trim();

    const corrections = {
      'slo': 'si',
      'svn': 'si',
      'cro': 'hr',
      'hrv': 'hr',
      'cze': 'cz',
      'ger': 'de',
      'deu': 'de',
      'aut': 'at',
      'pol': 'pl',
      'hun': 'hu',
      'ita': 'it',
      'fra': 'fr',
      'nld': 'nl',
      'bel': 'be',
      'dnk': 'dk',
      'swe': 'se',
      'nor': 'no',
      'fin': 'fi',
      'esp': 'es',
      'prt': 'pt',
      'gbr': 'gb',
      'uk': 'gb',
      'usa': 'us'
    };

    return corrections[normalized] || normalized;
  }

  /**
   * Animate zoom using requestAnimationFrame for smoother animation
   * @param {number} currentZoom - Starting zoom level
   * @param {number} targetZoom - Target zoom level
   * @param {number} duration - Animation duration in ms
   */
  function animateZoom(currentZoom, targetZoom, duration) {
    if (utils.animateValue) {
      utils.animateValue(currentZoom, targetZoom, duration, function(value) {
        map.setZoom(value);
      });
      return;
    }

    const startTime = performance.now();

    function tick(currentTime) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = progress * (2 - progress);
      const newZoom = currentZoom + (targetZoom - currentZoom) * eased;

      map.setZoom(newZoom);

      if (progress < 1) {
        requestAnimationFrame(tick);
      }
    }

    requestAnimationFrame(tick);
  }

  // =========================================================================
  // Initialization
  // =========================================================================

  /**
   * Initialize the store locator
   */
  function init() {
    elements.map = document.getElementById('store-locator-map');
    elements.searchInput = document.getElementById('store-search-input');
    elements.searchBtn = document.getElementById('store-search-btn');
    elements.locateBtn = document.getElementById('store-locate-btn');
    elements.countryFilter = document.getElementById('store-country-filter');
    elements.storeList = document.getElementById('store-list');
    elements.resultsCount = document.getElementById('store-results-count');
    elements.detailPanel = document.getElementById('store-detail-panel');
    elements.detailContent = document.getElementById('store-detail-content');
    elements.detailClose = document.getElementById('store-detail-close');
    elements.serviceFilters = document.querySelectorAll('.store-locator__service-input');

    const storeDataEl = document.getElementById('store-locations-data');
    if (storeDataEl) {
      try {
        stores = JSON.parse(storeDataEl.textContent);
      } catch (e) {
        console.error('[store-locator] Failed to parse store data', e);
        stores = [];
      }
    }

    bindEvents();
    handleUrlParameters();
  }

  /**
   * Handle URL parameters for preselecting store or running search
   */
  function handleUrlParameters() {
    const urlParams = new URLSearchParams(window.location.search);
    const storeHandle = urlParams.get('store');
    const searchQuery = urlParams.get('search');

    if (storeHandle) {
      waitForMap(function() {
        focusStore(storeHandle);
      });
    } else if (searchQuery) {
      if (elements.searchInput) {
        elements.searchInput.value = searchQuery;
      }
      waitForMap(function() {
        searchStores(searchQuery);
      });
    }
  }

  /**
   * Wait for map to be initialized before executing callback
   * @param {function} callback - Function to call when map is ready
   */
  function waitForMap(callback) {
    if (map) {
      callback();
    } else {
      const checkInterval = setInterval(function() {
        if (map) {
          clearInterval(checkInterval);
          callback();
        }
      }, 100);

      setTimeout(function() {
        clearInterval(checkInterval);
        callback();
      }, 5000);
    }
  }

  /**
   * Bind event listeners
   */
  function bindEvents() {
    if (elements.searchInput) {
      elements.searchInput.addEventListener('keyup', function(e) {
        if (e.key === 'Enter') {
          searchStores(this.value);
        }
      });
    }

    if (elements.searchBtn) {
      elements.searchBtn.addEventListener('click', function() {
        searchStores(elements.searchInput.value);
      });
    }

    if (elements.locateBtn) {
      elements.locateBtn.addEventListener('click', getUserLocation);
    }

    if (elements.countryFilter) {
      elements.countryFilter.addEventListener('change', applyFilters);
    }

    if (elements.serviceFilters) {
      for (const checkbox of elements.serviceFilters) {
        checkbox.addEventListener('change', applyFilters);
      }
    }

    if (elements.storeList) {
      elements.storeList.addEventListener('click', function(e) {
        const mapBtn = e.target.closest('.store-card__btn--map');
        if (mapBtn) {
          const handle = mapBtn.dataset.storeHandle;
          focusStore(handle);
        }
      });
    }

    if (elements.detailClose) {
      elements.detailClose.addEventListener('click', closeDetailPanel);
    }
  }

  // =========================================================================
  // Google Maps Implementation
  // =========================================================================

  /**
   * Initialize Google Map (called by Google Maps callback)
   */
  window.initStoreLocatorMap = function() {
    if (!elements.map) {
      elements.map = document.getElementById('store-locator-map');
    }

    if (!elements.map || typeof google === 'undefined') return;

    map = new google.maps.Map(elements.map, {
      center: { lat: parseFloat(config.defaultLat), lng: parseFloat(config.defaultLng) },
      zoom: parseInt(config.defaultZoom),
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      styles: getGoogleMapStyles()
    });

    createGoogleMarkers();

    if (config.enableClustering && window.MarkerClusterer) {
      initClustering();
    }

    if (markers.length > 0) {
      fitBoundsToMarkers();
    }
  };

  /**
   * Create Google Maps markers
   */
  function createGoogleMarkers() {
    for (const store of stores) {
      if (!store.lat || !store.lng) {
        console.warn(`[store-locator] Skipping "${store.name}" on map — missing coordinates`, store);
        continue;
      }

      const marker = new google.maps.Marker({
        position: { lat: parseFloat(store.lat), lng: parseFloat(store.lng) },
        map: map,
        title: store.name,
        icon: getGoogleMarkerIcon(store),
        storeData: store
      });

      const infoWindow = new google.maps.InfoWindow({
        content: createInfoWindowContent(store)
      });

      marker.addListener('click', function() {
        if (activePopup) {
          activePopup.close();
        }
        infoWindow.open(map, marker);
        activePopup = infoWindow;
        showDetailPanel(store);
        highlightStoreCard(store.handle);

        map.panTo(marker.getPosition());
        if (map.getZoom() < 15) {
          animateZoom(map.getZoom(), 15, 300);
        }
      });

      markers.push({ marker: marker, store: store, infoWindow: infoWindow });
    }
  }

  /**
   * Get Google marker icon
   * @param {object} store - Store data object
   * @returns {object|null} Google Maps icon configuration
   */
  function getGoogleMarkerIcon(store) {
    const countryCode = normalizeCountryCode(store.countryCode || '');

    if (config.markerStyle === 'flag' && countryCode) {
      const cacheKey = `marker-${countryCode}`;

      if (markerIconCache[cacheKey]) {
        return markerIconCache[cacheKey];
      }

      if (markerIconCache[`loading-${cacheKey}`]) {
        return {
          url: `https://flagcdn.com/w40/${countryCode}.png`,
          scaledSize: new google.maps.Size(30, 30),
          anchor: new google.maps.Point(15, 15)
        };
      }

      createCanvasMarker(countryCode, store);

      return {
        url: `https://flagcdn.com/w40/${countryCode}.png`,
        scaledSize: new google.maps.Size(30, 30),
        anchor: new google.maps.Point(15, 15)
      };
    } else if (config.markerStyle === 'pin') {
      return {
        path: google.maps.SymbolPath.CIRCLE,
        fillColor: store.isFlagship ? '#f4857b' : '#3d4246',
        fillOpacity: 1,
        strokeColor: '#fff',
        strokeWeight: 2,
        scale: 10
      };
    }

    return null;
  }

  /**
   * Create canvas-based marker with flag, circle border, and arrow
   * @param {string} countryCode - ISO country code
   * @param {object} store - Store data object
   */
  function createCanvasMarker(countryCode, store) {
    const cacheKey = `marker-${countryCode}`;
    markerIconCache[`loading-${cacheKey}`] = true;

    const canvas = document.createElement('canvas');
    canvas.width = 30;
    canvas.height = 44;
    const ctx = canvas.getContext('2d');

    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = function() {
      ctx.fillStyle = 'white';
      ctx.strokeStyle = 'black';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(15, 34);
      ctx.lineTo(7.5, 24);
      ctx.lineTo(22.5, 24);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = 'white';
      ctx.strokeStyle = 'black';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(15, 15, 14, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();

      ctx.save();
      ctx.beginPath();
      ctx.arc(15, 15, 13, 0, Math.PI * 2);
      ctx.clip();
      ctx.drawImage(img, 2, 2, 26, 26);
      ctx.restore();

      const iconUrl = canvas.toDataURL();
      markerIconCache[cacheKey] = {
        url: iconUrl,
        scaledSize: new google.maps.Size(30, 44),
        anchor: new google.maps.Point(15, 44)
      };

      delete markerIconCache[`loading-${cacheKey}`];

      for (const markerData of markers) {
        if (markerData.store && markerData.store.countryCode &&
            normalizeCountryCode(markerData.store.countryCode) === countryCode && markerData.marker) {
          markerData.marker.setIcon({
            url: iconUrl,
            scaledSize: new google.maps.Size(30, 44),
            anchor: new google.maps.Point(15, 44)
          });
        }
      }
    };

    img.onerror = function() {
      delete markerIconCache[`loading-${cacheKey}`];
    };

    img.src = `https://flagcdn.com/w40/${countryCode}.png`;
  }

  /**
   * Get Google Map styles
   * @returns {Array} Map style configuration
   */
  function getGoogleMapStyles() {
    return [
      {
        featureType: 'poi',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }]
      },
      {
        featureType: 'transit',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }]
      }
    ];
  }

  // =========================================================================
  // Store Display Functions
  // =========================================================================

  /**
   * Create info window content HTML
   * @param {object} store - Store data object
   * @returns {string} HTML content for info window
   */
  function createInfoWindowContent(store) {
    const strings = config.strings || {};
    const distanceHtml = store.distance !== undefined && store.distance !== Infinity
      ? `<span class="store-info-window__distance">${formatDistance(store.distance)}</span>`
      : '';

    return `
      <div class="store-info-window">
        ${distanceHtml}
        <h3 class="store-info-window__name">${escapeHtml(store.name)}</h3>
        ${store.isFlagship ? `<span class="store-info-window__badge">${strings.flagship || 'Flagship'}</span>` : ''}
        <p class="store-info-window__address">${escapeHtml(store.address || '').replace(/\n/g, '<br>')}</p>
        <p class="store-info-window__city">${escapeHtml(store.city)}, ${escapeHtml(store.country)}</p>
        ${store.phone ? `<p class="store-info-window__phone"><a href="tel:${store.phone.replace(/\s/g, '')}">${escapeHtml(store.phone)}</a></p>` : ''}
        <a href="https://www.google.com/maps/dir/?api=1&destination=${store.lat},${store.lng}"
           class="store-info-window__directions"
           target="_blank"
           rel="noopener">
          ${strings.getDirections || 'Get Directions'}
        </a>
      </div>
    `;
  }

  /**
   * Show detail panel with store information
   * @param {object} store - Store data object
   */
  function showDetailPanel(store) {
    if (!elements.detailPanel || !elements.detailContent) return;

    const strings = config.strings || {};
    let servicesHtml = '';

    const serviceNames = strings.serviceNames || {};

    if (store.services && store.services.length > 0) {
      servicesHtml = `
        <div class="store-detail__services">
          <h4>${strings.services || 'Services'}</h4>
          <ul>
            ${store.services.map(s => `<li>${escapeHtml(serviceNames[s] || s)}</li>`).join('')}
          </ul>
        </div>
      `;
    }

    let hoursHtml = '';
    if (store.openingHours) {
      hoursHtml = createOpeningHoursHtml(store.openingHours);
    }

    const distanceHtml = store.distance !== undefined && store.distance !== Infinity
      ? `<div class="store-detail__distance">
          <i class="at at-location"></i>
          <span>${formatDistance(store.distance)}</span>
          ${referenceLocation && referenceLocation.label ? `<small>${strings.fromLocation || 'from'} ${escapeHtml(referenceLocation.label)}</small>` : ''}
        </div>`
      : '';

    const countryCode = normalizeCountryCode(store.countryCode || '');

    elements.detailContent.innerHTML = `
      <div class="store-detail">
        ${store.image ? `<img src="${store.image}" alt="${escapeHtml(store.name)}" class="store-detail__image">` : ''}
        <div class="store-detail__header">
          <img src="https://flagcdn.com/w40/${countryCode}.png"
               alt="${store.country}"
               class="store-detail__flag"
               width="40" height="30"
               onerror="this.style.display='none'">
          <div>
            <h3 class="store-detail__name">${escapeHtml(store.name)}</h3>
            ${store.isFlagship ? `<span class="store-detail__badge">${strings.flagship || 'Flagship Store'}</span>` : ''}
          </div>
        </div>
        ${distanceHtml}
        <address class="store-detail__address">
          ${escapeHtml(store.address || '').replace(/\n/g, '<br>')}<br>
          ${escapeHtml(store.city)}, ${escapeHtml(store.country)}
        </address>
        ${store.phone ? `
          <p class="store-detail__contact">
            <i class="at at-phone"></i>
            <a href="tel:${store.phone.replace(/\s/g, '')}">${escapeHtml(store.phone)}</a>
          </p>
        ` : ''}
        ${store.email ? `
          <p class="store-detail__contact">
            <i class="at at-envelope"></i>
            <a href="mailto:${store.email}">${escapeHtml(store.email)}</a>
          </p>
        ` : ''}
        ${hoursHtml}
        ${servicesHtml}
        <a href="https://www.google.com/maps/dir/?api=1&destination=${store.lat},${store.lng}"
           class="store-detail__directions"
           target="_blank"
           rel="noopener">
          <i class="at at-directions"></i>
          ${strings.getDirections || 'Get Directions'}
        </a>
      </div>
    `;

    elements.detailPanel.style.display = 'block';
  }

  /**
   * Create opening hours HTML
   * @param {object|string} hours - Opening hours data
   * @returns {string} HTML for opening hours display
   */
  function createOpeningHoursHtml(hours) {
    const strings = config.strings || {};
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const dayNames = {
      monday: strings.monday || 'Monday',
      tuesday: strings.tuesday || 'Tuesday',
      wednesday: strings.wednesday || 'Wednesday',
      thursday: strings.thursday || 'Thursday',
      friday: strings.friday || 'Friday',
      saturday: strings.saturday || 'Saturday',
      sunday: strings.sunday || 'Sunday'
    };

    let parsedHours = hours;
    if (typeof hours === 'string') {
      try {
        parsedHours = JSON.parse(hours);
      } catch (e) {
        return '';
      }
    }

    if (!parsedHours || typeof parsedHours !== 'object') return '';

    const currentDay = days[new Date().getDay() === 0 ? 6 : new Date().getDay() - 1];

    let html = `<div class="store-detail__hours"><h4>${strings.openingHours || 'Opening Hours'}</h4><table>`;

    for (const day of days) {
      const dayData = parsedHours[day];
      const isToday = day === currentDay;
      let timeStr = strings.closed || 'Closed';

      if (dayData && !dayData.closed) {
        timeStr = `${dayData.open} - ${dayData.close}`;
      }

      html += `
        <tr class="${isToday ? 'store-detail__hours-today' : ''}">
          <td>${dayNames[day]}</td>
          <td>${timeStr}</td>
        </tr>
      `;
    }

    html += '</table></div>';
    return html;
  }

  /**
   * Close detail panel
   */
  function closeDetailPanel() {
    if (elements.detailPanel) {
      elements.detailPanel.style.display = 'none';
    }
  }

  /**
   * Highlight store card in the list
   * @param {string} handle - Store handle
   */
  function highlightStoreCard(handle) {
    if (!elements.storeList) return;

    const items = elements.storeList.querySelectorAll('.store-locator__list-item');
    for (const item of items) {
      item.classList.remove('is-active');
      if (item.dataset.storeId === handle) {
        item.classList.add('is-active');
        item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }

  /**
   * Focus on a specific store
   * @param {string} handle - Store handle
   */
  function focusStore(handle) {
    const store = stores.find(s => s.handle === handle);
    if (!store) return;

    highlightStoreCard(handle);
    showDetailPanel(store);

    if (map && typeof google !== 'undefined') {
      const markerData = markers.find(m => m.store && m.store.handle === handle);

      map.setCenter({ lat: parseFloat(store.lat), lng: parseFloat(store.lng) });
      map.setZoom(15);

      if (markerData) {
        google.maps.event.trigger(markerData.marker, 'click');
      }
    }
  }

  // =========================================================================
  // Search & Filter Functions
  // =========================================================================

  /**
   * Search stores by query - geocode to find location, then show stores by distance
   * @param {string} query - Search query
   */
  function searchStores(query) {
    if (!query.trim()) {
      clearReferenceLocation();
      showAllStores();
      return;
    }

    geocodeAddress(query);
  }

  /**
   * Geocode an address and calculate distances from it
   * @param {string} address - Address to geocode
   */
  function geocodeAddress(address) {
    if (typeof google === 'undefined' || !google.maps || !google.maps.Geocoder) {
      textSearchStores(address);
      return;
    }

    const geocoder = new google.maps.Geocoder();

    if (elements.searchBtn) {
      elements.searchBtn.classList.add('is-loading');
    }

    geocoder.geocode({ address: address }, function(results, status) {
      if (elements.searchBtn) {
        elements.searchBtn.classList.remove('is-loading');
      }

      if (status === 'OK' && results[0]) {
        const location = results[0].geometry.location;
        setReferenceLocation({
          lat: location.lat(),
          lng: location.lng(),
          label: results[0].formatted_address
        });
      } else {
        textSearchStores(address);
      }
    });
  }

  /**
   * Filter stores by text query (fallback when geocoding fails)
   * @param {string} query - Search query
   */
  function textSearchStores(query) {
    const searchQuery = query.toLowerCase().trim();

    const textMatches = stores.filter(function(store) {
      return (
        (store.name && store.name.toLowerCase().includes(searchQuery)) ||
        (store.city && store.city.toLowerCase().includes(searchQuery)) ||
        (store.country && store.country.toLowerCase().includes(searchQuery)) ||
        (store.address && store.address.toLowerCase().includes(searchQuery))
      );
    });

    updateVisibleStores(textMatches);
  }

  /**
   * Set reference location and update distances
   * @param {object} location - Location with lat, lng, and optional label
   */
  function setReferenceLocation(location) {
    referenceLocation = location;

    sortStoresByDistance({ lat: location.lat, lng: location.lng });
    updateReferenceLocationDisplay();

    if (map && typeof google !== 'undefined') {
      map.setCenter({ lat: location.lat, lng: location.lng });
      map.setZoom(10);

      if (referenceMarker) {
        referenceMarker.setMap(null);
      }

      referenceMarker = new google.maps.Marker({
        position: { lat: location.lat, lng: location.lng },
        map: map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: '#4285f4',
          fillOpacity: 1,
          strokeColor: '#fff',
          strokeWeight: 2,
          scale: 8
        },
        title: location.label || 'Reference location'
      });
    }

    showAllStores();
  }

  /**
   * Clear reference location
   */
  function clearReferenceLocation() {
    referenceLocation = null;

    if (referenceMarker) {
      referenceMarker.setMap(null);
      referenceMarker = null;
    }

    if (elements.storeList) {
      const distances = elements.storeList.querySelectorAll('.store-card__distance');
      for (const el of distances) {
        el.remove();
      }
    }

    for (const store of stores) {
      delete store.distance;
    }

    const refDisplay = document.querySelector('.store-locator__reference-location');
    if (refDisplay) {
      refDisplay.style.display = 'none';
    }
  }

  /**
   * Update reference location display in sidebar
   */
  function updateReferenceLocationDisplay() {
    let refDisplay = document.querySelector('.store-locator__reference-location');

    if (!referenceLocation) {
      if (refDisplay) {
        refDisplay.style.display = 'none';
      }
      return;
    }

    if (!refDisplay && elements.resultsCount) {
      refDisplay = document.createElement('div');
      refDisplay.className = 'store-locator__reference-location';
      elements.resultsCount.parentNode.insertBefore(refDisplay, elements.resultsCount);
    }

    if (refDisplay) {
      const strings = config.strings || {};
      refDisplay.innerHTML = `
        <span class="store-locator__reference-label">
          <i class="at at-location"></i>
          ${strings.distanceFrom || 'Distance from'}:
        </span>
        <span class="store-locator__reference-address">${escapeHtml(referenceLocation.label || '')}</span>
        <button type="button" class="store-locator__reference-clear" aria-label="${strings.clear || 'Clear'}">
          <i class="at at-times-l"></i>
        </button>
      `;
      refDisplay.style.display = 'flex';

      const clearBtn = refDisplay.querySelector('.store-locator__reference-clear');
      if (clearBtn) {
        clearBtn.addEventListener('click', function() {
          clearReferenceLocation();
          if (elements.searchInput) {
            elements.searchInput.value = '';
          }
          showAllStores();
        });
      }
    }
  }

  /**
   * Apply country and service filters together
   */
  function applyFilters() {
    const selectedCountry = elements.countryFilter ? elements.countryFilter.value : '';
    const selectedServices = [];

    if (elements.serviceFilters) {
      for (const cb of elements.serviceFilters) {
        if (cb.checked) selectedServices.push(cb.value);
      }
    }

    let filtered = stores;

    if (selectedCountry) {
      filtered = filtered.filter(s => s.country === selectedCountry);
    }

    if (selectedServices.length > 0) {
      filtered = filtered.filter(s =>
        s.services && selectedServices.every(svc => s.services.includes(svc))
      );
    }

    if (!selectedCountry && selectedServices.length === 0) {
      showAllStores();
    } else {
      updateVisibleStores(filtered);
    }
  }

  /**
   * Update visible stores on map and list
   * @param {Array} visibleStores - Array of stores to show
   */
  function updateVisibleStores(visibleStores) {
    const visibleHandles = visibleStores.map(s => s.handle);

    for (const markerData of markers) {
      const isVisible = markerData.store && visibleHandles.includes(markerData.store.handle);

      if (markerData.marker && markerData.marker.setVisible) {
        markerData.marker.setVisible(isVisible);
      }
    }

    if (elements.storeList) {
      const items = elements.storeList.querySelectorAll('.store-locator__list-item');
      for (const item of items) {
        const isVisible = visibleHandles.includes(item.dataset.storeId);
        item.style.display = isVisible ? '' : 'none';
      }
    }

    updateResultsCount(visibleStores.length);

    if (visibleStores.length > 0) {
      fitBoundsToMarkers(visibleStores);
    }
  }

  /**
   * Show all stores
   */
  function showAllStores() {
    for (const markerData of markers) {
      if (markerData.marker && markerData.marker.setVisible) {
        markerData.marker.setVisible(true);
      }
    }

    if (elements.storeList) {
      const items = elements.storeList.querySelectorAll('.store-locator__list-item');
      for (const item of items) {
        item.style.display = '';
      }
    }

    updateResultsCount(stores.length);
    fitBoundsToMarkers();
  }

  /**
   * Update results count display
   * @param {number} count - Number of stores found
   */
  function updateResultsCount(count) {
    if (elements.resultsCount) {
      elements.resultsCount.textContent = `${count} ${config.strings.storesFound || 'stores found'}`;
    }
  }

  /**
   * Get user's current location
   */
  function getUserLocation() {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    if (elements.locateBtn) {
      elements.locateBtn.classList.add('is-loading');
    }

    navigator.geolocation.getCurrentPosition(
      function(position) {
        userLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };

        if (elements.locateBtn) {
          elements.locateBtn.classList.remove('is-loading');
        }

        const strings = config.strings || {};
        setReferenceLocation({
          lat: userLocation.lat,
          lng: userLocation.lng,
          label: strings.yourLocation || 'Your location'
        });
      },
      function(error) {
        alert('Unable to get your location. Please try searching instead.');
        if (elements.locateBtn) {
          elements.locateBtn.classList.remove('is-loading');
        }
      }
    );
  }

  /**
   * Sort stores by distance from a point
   * @param {{lat: number, lng: number}} origin - Reference point
   */
  function sortStoresByDistance(origin) {
    for (const store of stores) {
      if (store.lat && store.lng) {
        store.distance = calculateDistance(origin, { lat: parseFloat(store.lat), lng: parseFloat(store.lng) });
      } else {
        store.distance = Infinity;
      }
    }

    stores.sort((a, b) => a.distance - b.distance);

    if (elements.storeList) {
      for (const store of stores) {
        const item = elements.storeList.querySelector(`[data-store-id="${store.handle}"]`);
        if (item) {
          elements.storeList.appendChild(item);

          let distanceEl = item.querySelector('.store-card__distance');
          if (store.distance !== Infinity) {
            if (distanceEl) {
              distanceEl.textContent = formatDistance(store.distance);
              distanceEl.style.display = '';
            } else {
              const content = item.querySelector('.store-card__content');
              if (content) {
                const distSpan = document.createElement('span');
                distSpan.className = 'store-card__distance';
                distSpan.textContent = formatDistance(store.distance);
                content.insertBefore(distSpan, content.firstChild);
              }
            }
          } else if (distanceEl) {
            distanceEl.style.display = 'none';
          }
        }
      }
    }

    for (const markerData of markers) {
      if (markerData.infoWindow && markerData.store) {
        markerData.infoWindow.setContent(createInfoWindowContent(markerData.store));
      }
    }
  }

  /**
   * Fit map bounds to show all/visible markers
   * @param {Array} [storeList] - Optional list of stores to fit bounds to
   */
  function fitBoundsToMarkers(storeList) {
    if (!map || markers.length === 0 || typeof google === 'undefined') return;

    const targetStores = storeList || stores;
    const targetHandles = targetStores.map(s => s.handle);

    const bounds = new google.maps.LatLngBounds();
    let hasVisibleMarkers = false;

    for (const markerData of markers) {
      const marker = markerData.marker;
      if (marker && marker.getVisible && marker.getVisible() && markerData.store && targetHandles.includes(markerData.store.handle)) {
        bounds.extend(marker.getPosition());
        hasVisibleMarkers = true;
      }
    }

    if (hasVisibleMarkers) {
      map.fitBounds(bounds);

      google.maps.event.addListenerOnce(map, 'idle', function() {
        if (map.getZoom() > 15) {
          map.setZoom(15);
        }
      });
    }
  }

  /**
   * Initialize marker clustering
   */
  function initClustering() {
    if (!window.MarkerClusterer) return;

    if (markerCluster) {
      markerCluster.clearMarkers();
    }

    const googleMarkers = markers.map(m => m.marker).filter(m => m);
    markerCluster = new markerClusterer.MarkerClusterer({
      map: map,
      markers: googleMarkers
    });
  }

  // =========================================================================
  // Header Search API (for external use)
  // =========================================================================

  window.StoreLocatorSearch = {
    stores: [],

    init: function(storesData) {
      this.stores = storesData || [];
    },

    search: function(query, userLat, userLng) {
      if (!query.trim() && !userLat) {
        return [];
      }

      let results = this.stores;

      if (query.trim()) {
        const searchQuery = query.toLowerCase().trim();
        results = results.filter(function(store) {
          return (
            (store.name && store.name.toLowerCase().includes(searchQuery)) ||
            (store.city && store.city.toLowerCase().includes(searchQuery)) ||
            (store.country && store.country.toLowerCase().includes(searchQuery))
          );
        });
      }

      if (userLat && userLng) {
        const origin = { lat: userLat, lng: userLng };
        for (const store of results) {
          if (store.lat && store.lng) {
            store.distance = calculateDistance(origin, { lat: parseFloat(store.lat), lng: parseFloat(store.lng) });
          } else {
            store.distance = Infinity;
          }
        }
        results.sort((a, b) => a.distance - b.distance);
      }

      return results.slice(0, 5);
    },

    getNearest: function(userLat, userLng, count) {
      if (!userLat || !userLng) return [];

      const origin = { lat: userLat, lng: userLng };
      const withDistance = this.stores.map(function(store) {
        return {
          ...store,
          distance: store.lat && store.lng ?
            calculateDistance(origin, { lat: parseFloat(store.lat), lng: parseFloat(store.lng) }) :
            Infinity
        };
      });

      return withDistance
        .filter(s => s.distance !== Infinity)
        .sort((a, b) => a.distance - b.distance)
        .slice(0, count || 5);
    }
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
